import { createSelector, Selector, Store } from '@ngrx/store'
import { selectionFeature, topLevelSelectionName } from './selection.reducer'
import {
    findSelectionNodeByIdInRoot,
    findSelectionNodeInRoot,
    FreeSelection,
    getAllChildren,
    getListableSelectionNodes,
    getAllChildrenPointKeysInRootOrNode,
    getAllChildrenSelectionTreeItems,
    getSelectionTreeItemLevel,
    getEditableSelectionBuckets,
    getEditableSelectionSize,
    getAllBucketSegSources,
    getHierarchicalRootSegSources,
    getPointKeysForSelection,
    getPointsForSegSource,
    getTotalPointsSize,
    HierarchicalSelectionNode,
    SelectionBucket,
    JobSelectionRef,
    SelectionBase,
    SelectionLayerInfo,
    SelectionsForCaseList,
    SelectionState,
    SelectionTreeItem,
    SelectionType,
    selectionSpansMultipleSegSources,
    selectionBelongsOnlyToSegSource,
    sameStringSet,
} from './selection.state'
import { SelectionLayer } from '../../shared/model/layers/selection-layer'

import { getPlotData } from '../plot-data/plot-data'
import {
    selectCaseSegObjectPropertyMap,
    selectCaseSegObjectPropertyMaps,
    selectLoadedSegSources,
} from '../case/case.selectors'

export const defaultHierarchicalSelectionNodeItem: SelectionTreeItem = {
    groupName: undefined,
    name: topLevelSelectionName,
    level: 0,
    size: getPlotData().points.length,
    id: '',
    path: [topLevelSelectionName],
}

export const { selectSelectionState } = selectionFeature

export const {
    selectMode,
    selectFreeSelection,
    selectHierarchicalSelection,
    selectUnsupervisedSelection,
    selectSavedFreeSelections,
    selectSavedHierarchicalSelections,
    selectSavedUnsupervisedSelections,
} = selectionFeature

export const selectActiveSegSource = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.activeSegSource
)

export const selectEditableSelectionBuckets = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionBucket[] => getEditableSelectionBuckets(state)
)

export const selectSelectionSize = createSelector(
    selectSelectionState,
    (state: SelectionState): number => getEditableSelectionSize(state)
)

export const selectSelectionPoints = createSelector(
    selectSelectionState,
    selectActiveSegSource,
    (state: SelectionState, activeSegSource: string | undefined): Set<number> => {
        if (state.type == SelectionType.Hierarchical) {
            return getPointsForSegSource(state.hierarchicalSelection.unsavedLevel, activeSegSource)
        } else if (state.type == SelectionType.Unsupervised) {
            return getPointsForSegSource(state.unsupervisedSelection.unsavedLevel, activeSegSource)
        } else {
            return getPointsForSegSource(state.freeSelection, activeSegSource)
        }
    }
)

export const selectSelectionType = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionType => {
        return state.type
    }
)

export const selectHierarchicalSelectionIsRoot = createSelector(
    selectSelectionState,
    (state: SelectionState): boolean => {
        if (state.type === SelectionType.Hierarchical) {
            return state.hierarchicalSelection.selectedHierarchyName === undefined
        } else {
            return state.unsupervisedSelection.selectedUnsupervisedName === undefined
        }
    }
)

export const selectCaseHasSavedFreeSelections = createSelector(
    selectSelectionState,
    (selectionState: SelectionState): boolean => {
        return selectionState.savedFreeSelections.size > 0
    }
)

export const selectFreeSelectionName = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.freeSelection.name
)

/** Warn when a named free selection is active but loaded intensity maps ≠ saved bucket segSources. */
export const selectSavedFreeSelectionIntensityMapsMismatch = createSelector(
    selectSelectionType,
    selectFreeSelectionName,
    selectSavedFreeSelections,
    selectLoadedSegSources,
    selectCaseSegObjectPropertyMaps,
    (type, name, savedSelections, loadedSegSources, allMaps): string | null => {
        if (type !== SelectionType.Free || !name) return null
        const saved = savedSelections.get(name)
        if (!saved) return null

        const savedSources = getAllBucketSegSources(saved)
        if (savedSources.length === 0) return null
        if (sameStringSet(savedSources, loadedSegSources)) return null

        const nameBySource = new Map(
            allMaps
                .filter(m => !!m.segSource && !!m.name?.trim())
                .map(m => [m.segSource!, m.name!.trim()] as const)
        )
        const formatSources = (sources: string[]): string =>
            [...sources]
                .map(s => nameBySource.get(s))
                .filter((label): label is string => !!label)
                .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                .join(', ')

        const savedMapLabels = formatSources(savedSources)
        const savedMapsClause = savedMapLabels
            ? ` (${savedMapLabels})`
            : ''

        return (
            `Selected intensity maps don’t match those used when “${name}” was saved` +
            `${savedMapsClause}. Editing may not cover all of its cells.`
        )
    }
)

export const selectFreeSelectionColor = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => state.freeSelection.annotationColor
)

export const selectHierarchicalSelectionRootName = createSelector(
    selectSelectionState,
    (state: SelectionState): string | undefined => {
        if (state.type === SelectionType.Hierarchical) {
            return state.hierarchicalSelection.name
        } else {
            return state.unsupervisedSelection.name
        }
    }
)

export const selectHierarchicalSelectionColor = createSelector(
    selectSelectionState,
    (state: SelectionState): { selectedLevel: string; unsavedLevel: string } | undefined => {
        if (state.type !== SelectionType.Hierarchical && state.type !== SelectionType.Unsupervised) {
            return undefined
        }

        let selectedHierarchyName: string | undefined
        if (state.type === SelectionType.Hierarchical) {
            if (!state.hierarchicalSelection.children) return
            selectedHierarchyName = state.hierarchicalSelection.selectedHierarchyName
            const selectedHierarchy = getSelectedHierarchy(state.hierarchicalSelection.children)

            return {
                selectedLevel:
                    selectedHierarchy?.annotationColor ??
                    state.hierarchicalSelection.unsavedLevel.annotationColor,
                unsavedLevel: state.hierarchicalSelection.unsavedLevel.annotationColor,
            }
        } else {
            if (!state.unsupervisedSelection.children) return
            selectedHierarchyName = state.unsupervisedSelection.selectedUnsupervisedName
            const selectedUnsupervised = getSelectedHierarchy(state.unsupervisedSelection.children)

            return {
                selectedLevel:
                    selectedUnsupervised?.annotationColor ??
                    state.unsupervisedSelection.unsavedLevel.annotationColor,
                unsavedLevel: state.unsupervisedSelection.unsavedLevel.annotationColor,
            }
        }

        function getSelectedHierarchy(children: HierarchicalSelectionNode[]): HierarchicalSelectionNode | undefined {
            if (!children) return undefined
            for (const c of children) {
                if (c.name === selectedHierarchyName) {
                    return c
                }
                const selectedHierarchy = getSelectedHierarchy(c.children ?? [])
                if (selectedHierarchy) {
                    return selectedHierarchy
                }
            }
            return undefined
        }
    }
)

export const selectHierarchicalSelectionNodeItem = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem => {
        if (state.type === SelectionType.Hierarchical) {
            if (state.hierarchicalSelection.selectedHierarchyName) {
                const node = findSelectionNodeInRoot(
                    state.hierarchicalSelection,
                    state.hierarchicalSelection.selectedHierarchyName
                )
                return {
                    groupName: state.hierarchicalSelection.name,
                    name: state.hierarchicalSelection.selectedHierarchyName,
                    level: getSelectionTreeItemLevel(
                        state.hierarchicalSelection,
                        state.hierarchicalSelection.selectedHierarchyName
                    ),
                    size: node ? getTotalPointsSize(node) : 0,
                    id: node?.id ?? '',
                    path: [
                        state.hierarchicalSelection.name!,
                        state.hierarchicalSelection.selectedHierarchyName,
                    ],
                }
            }
        } else if (state.type === SelectionType.Unsupervised) {
            if (state.unsupervisedSelection.selectedUnsupervisedName) {
                const node = findSelectionNodeInRoot(
                    state.unsupervisedSelection,
                    state.unsupervisedSelection.selectedUnsupervisedName
                )
                return {
                    groupName: state.unsupervisedSelection.name,
                    name: state.unsupervisedSelection.selectedUnsupervisedName,
                    level: getSelectionTreeItemLevel(
                        state.unsupervisedSelection,
                        state.unsupervisedSelection.selectedUnsupervisedName
                    ),
                    size: node ? getTotalPointsSize(node) : 0,
                    id: node?.id ?? '',
                    path: [
                        state.unsupervisedSelection.name!,
                        state.unsupervisedSelection.selectedUnsupervisedName,
                    ],
                }
            }
        }

        return defaultHierarchicalSelectionNodeItem
    }
)

export const selectSavedFreeSelectionNames = createSelector(
    selectSelectionState,
    (selectionState: SelectionState): string[] => {
        return Array.from(selectionState.savedFreeSelections)
            .map(([key]) => key)
            .sort((a, b) => a.localeCompare(b))
    }
)

export const selectSavedFreeSelectionsForJobs = createSelector(
    selectSelectionState,
    (selectionState: SelectionState): FreeSelection[] => {
        return [...selectionState.savedFreeSelections.values()].sort((a, b) =>
            (a.name ?? '').localeCompare(b.name ?? '')
        )
    }
)

export interface JobSelectionListItem extends JobSelectionRef {
    name: string
    spansMultipleSegSources: boolean
    /** Hierarchical/unsupervised tree name; used for ancestry checks within one root. */
    rootName?: string
}

export const selectJobSelectionOptions = createSelector(
    selectSelectionState,
    (state): JobSelectionListItem[] => {
        const free: JobSelectionListItem[] = [...state.savedFreeSelections.values()].map(s => ({
            name: s.name!,
            id: s.id,
            type: 1,
            spansMultipleSegSources: selectionSpansMultipleSegSources(s),
        }))
        const hierarchical: JobSelectionListItem[] = [
            ...state.savedHierarchicalSelections.entries(),
        ].flatMap(([groupName, root]) =>
            getAllChildrenSelectionTreeItems(root).map(t => {
                const node = findSelectionNodeByIdInRoot(root, t.id)
                return {
                    name: `[${groupName}] ${t.name}`,
                    id: t.id,
                    type: 2 as const,
                    path: t.path,
                    rootName: groupName,
                    spansMultipleSegSources: node
                        ? selectionSpansMultipleSegSources(node)
                        : false,
                }
            })
        )
        const unsupervised: JobSelectionListItem[] = [
            ...state.savedUnsupervisedSelections.entries(),
        ].flatMap(([groupName, root]) =>
            getAllChildrenSelectionTreeItems(root).map(t => {
                const node = findSelectionNodeByIdInRoot(root, t.id)
                return {
                    name: `[${groupName}] ${t.name}`,
                    id: t.id,
                    type: 3 as const,
                    path: t.path,
                    rootName: groupName,
                    spansMultipleSegSources: node
                        ? selectionSpansMultipleSegSources(node)
                        : false,
                }
            })
        )
        return [...free, ...hierarchical, ...unsupervised].sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }
)

/** Job selection options whose points belong only to the current intensity map's segSource. */
export const selectPlottingJobSelectionOptions = createSelector(
    selectSelectionState,
    selectCaseSegObjectPropertyMap,
    (state, intensityMap): JobSelectionListItem[] => {
        const segSource = intensityMap?.segSource
        if (!segSource) return []

        const free: JobSelectionListItem[] = [...state.savedFreeSelections.values()]
            .filter(s => selectionBelongsOnlyToSegSource(s, segSource))
            .map(s => ({
                name: s.name!,
                id: s.id,
                type: 1 as const,
                spansMultipleSegSources: false,
            }))

        const hierarchical: JobSelectionListItem[] = [
            ...state.savedHierarchicalSelections.entries(),
        ].flatMap(([groupName, root]) =>
            getAllChildrenSelectionTreeItems(root).flatMap(t => {
                const node = findSelectionNodeByIdInRoot(root, t.id)
                if (!node || !selectionBelongsOnlyToSegSource(node, segSource)) return []
                return [
                    {
                        name: `[${groupName}] ${t.name}`,
                        id: t.id,
                        type: 2 as const,
                        path: t.path,
                        rootName: groupName,
                        spansMultipleSegSources: false,
                    },
                ]
            })
        )

        const unsupervised: JobSelectionListItem[] = [
            ...state.savedUnsupervisedSelections.entries(),
        ].flatMap(([groupName, root]) =>
            getAllChildrenSelectionTreeItems(root).flatMap(t => {
                const node = findSelectionNodeByIdInRoot(root, t.id)
                if (!node || !selectionBelongsOnlyToSegSource(node, segSource)) return []
                return [
                    {
                        name: `[${groupName}] ${t.name}`,
                        id: t.id,
                        type: 3 as const,
                        path: t.path,
                        rootName: groupName,
                        spansMultipleSegSources: false,
                    },
                ]
            })
        )

        return [...free, ...hierarchical, ...unsupervised].sort((a, b) =>
            a.name.localeCompare(b.name)
        )
    }
)

export const selectSavedHierarchicalSelectionRootNames = createSelector(
    selectSelectionState,
    selectLoadedSegSources,
    (selectionState: SelectionState, loadedSegSources: string[]): string[] => {
        const map =
            selectionState.type === SelectionType.Hierarchical
                ? selectionState.savedHierarchicalSelections
                : selectionState.savedUnsupervisedSelections
        return Array.from(map.entries())
            .filter(([, root]) => {
                const rootSources = getHierarchicalRootSegSources(root)
                // Hide trees with no resolvable footprint until loaded maps are known.
                if (rootSources.length === 0) return loadedSegSources.length === 0
                return sameStringSet(rootSources, loadedSegSources)
            })
            .map(([name]) => name)
            .sort((a, b) => a.localeCompare(b))
    }
)

export const selectCaseHasSavedHierarchicalSelections = createSelector(
    selectSavedHierarchicalSelectionRootNames,
    (names: string[]): boolean => names.length > 0
)

export const selectSavedHierarchicalSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem[] => {
        if (!state.hierarchicalSelection.name) return []
        const selection = state.savedHierarchicalSelections.get(state.hierarchicalSelection.name!)

        if (selection) {
            return getAllChildrenSelectionTreeItems(selection)
        }

        return []
    }
)

export const selectAllSavedHierarchicalSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): { groupName: string; tree: SelectionTreeItem[] }[] => {
        return Array.from(state.savedHierarchicalSelections.entries()).map(
            ([name, selection]) => ({
                groupName: name,
                tree: getAllChildrenSelectionTreeItems(selection),
            })
        )
    }
)

export const selectSavedUnsupervisedSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionTreeItem[] => {
        if (!state.unsupervisedSelection.name) return []
        const selection = state.savedUnsupervisedSelections.get(state.unsupervisedSelection.name)
        if (selection) {
            return getAllChildrenSelectionTreeItems(selection)
        } else {
            return []
        }
    }
)

export const selectAllSavedUnsupervisedSelectionNodeItems = createSelector(
    selectSelectionState,
    (state: SelectionState): { groupName: string; tree: SelectionTreeItem[] }[] => {
        return Array.from(state.savedUnsupervisedSelections.entries()).map(
            ([name, selection]) => ({
                groupName: name,
                tree: getAllChildrenSelectionTreeItems(selection),
            })
        )
    }
)

export const selectSavedAndCurrentFreeSelections = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionsForCaseList => {
        let currentSelection: FreeSelection | null = state.freeSelection

        if (!state.freeSelection.modified) {
            currentSelection = null
        }
        return currentSelection
            ? [currentSelection, ...state.savedFreeSelections.values()]
            : [...state.savedFreeSelections.values()]
    }
)

export const selectDisplayedOnScatterPlot = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionBase[] => {
        return state.displayedOnScatterPlot
    }
)

export const selectSavedAndCurrentFreeSelectionsAsLayers = (
    store: Store
): Selector<SelectionState, SelectionLayer[]> => {
    let lastResult: SelectionLayer[] = []
    return createSelector(selectSelectionState, (state: SelectionState): SelectionLayer[] => {
        let currentSelection: SelectionLayerInfo | null = {
            ...state.freeSelection,
            type: SelectionType.Free,
            rootName: undefined,
        }
        const savedSelections: SelectionLayerInfo[] = [...state.savedFreeSelections.values()]
            .filter(selection => selection.name)
            .map(selection => ({
                ...selection,
                modified:
                    selection.name === state.freeSelection.name && state.freeSelection.modified,
                type: SelectionType.Free,
                rootName: undefined,
            }))
            .sort((a, b) => a.name!.localeCompare(b.name!))

        if (
            !state.freeSelection.modified ||
            !savedSelections.every(selection => !selection.modified)
        ) {
            currentSelection = null
        }

        const displaySelections: SelectionLayerInfo[] = currentSelection
            ? [currentSelection, ...savedSelections]
            : savedSelections

        const displayLayers = convertSelectionLayerInfoToSelectionLayers(
            displaySelections,
            lastResult,
            store
        )
        lastResult = displayLayers
        return displayLayers
    })
}

export const selectSavedAndCurrentHierarchicalSelections = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionsForCaseList => {
        let currentSelections: HierarchicalSelectionNode[] | null = []

        if (getTotalPointsSize(state.hierarchicalSelection.unsavedLevel) > 0) {
            currentSelections = [
                {
                    ...state.hierarchicalSelection.unsavedLevel,
                    children: undefined,
                },
            ]
        }

        const savedSelections: HierarchicalSelectionNode[] = [
            ...state.savedHierarchicalSelections.values(),
        ]
            .map(selection => getAllChildren(selection))
            .flat()
            .map(selection => ({
                ...selection,
                modified: false,
            }))

        return currentSelections ? [...currentSelections, ...savedSelections] : [...savedSelections]
    }
)

export const selectSavedAndCurrentUnsupervisedSelections = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionsForCaseList => {
        let currentSelections: HierarchicalSelectionNode[] | null = []

        if (getTotalPointsSize(state.unsupervisedSelection.unsavedLevel) > 0) {
            currentSelections = [
                {
                    ...state.unsupervisedSelection.unsavedLevel,
                    children: undefined,
                },
            ]
        }

        const savedSelections: HierarchicalSelectionNode[] = [
            ...state.savedUnsupervisedSelections.values(),
        ]
            .map(selection => getAllChildren(selection))
            .flat()
            .map(selection => ({
                ...selection,
                modified: false,
            }))

        return currentSelections ? [...currentSelections, ...savedSelections] : [...savedSelections]
    }
)

export const selectSavedAndCurrentHierarchicalSelectionsAsLayers = (
    store: Store
): Selector<SelectionState, SelectionLayer[]> => {
    let lastResult: SelectionLayer[] = []
    return createSelector(selectSelectionState, (state: SelectionState): SelectionLayer[] => {
        let currentSelections: SelectionLayerInfo[] | null = []

        if (getTotalPointsSize(state.hierarchicalSelection.unsavedLevel) > 0) {
            currentSelections = [
                {
                    ...state.hierarchicalSelection.unsavedLevel,
                    type: SelectionType.Hierarchical,
                    rootName: state.hierarchicalSelection.name,
                },
            ]
        }

        const savedSelections: SelectionLayerInfo[] = [
            ...state.savedHierarchicalSelections.values(),
        ]
            .map(selectionRoot => {
                return getListableSelectionNodes(selectionRoot).map(selection => ({
                    ...selection,
                    modified: false,
                    type: SelectionType.Hierarchical,
                    rootName: selectionRoot.name,
                }))
            })
            .flat()

        const displaySelections: SelectionLayerInfo[] = [...currentSelections, ...savedSelections]

        const displayLayers = convertSelectionLayerInfoToSelectionLayers(
            displaySelections,
            lastResult,
            store
        )
        lastResult = displayLayers
        return displayLayers
    })
}

export const selectSavedAndCurrentUnsupervisedSelectionsAsLayers = (
    store: Store
): Selector<SelectionState, SelectionLayer[]> => {
    let lastResult: SelectionLayer[] = []
    return createSelector(selectSelectionState, (state: SelectionState): SelectionLayer[] => {
        let currentSelections: SelectionLayerInfo[] | null = []

        if (getTotalPointsSize(state.unsupervisedSelection.unsavedLevel) > 0) {
            currentSelections = [
                {
                    ...state.unsupervisedSelection.unsavedLevel,
                    type: SelectionType.Unsupervised,
                    rootName: state.unsupervisedSelection.name,
                },
            ]
        }

        const savedSelections: SelectionLayerInfo[] = [
            ...state.savedUnsupervisedSelections.values(),
        ]
            .map(selectionRoot => {
                return getListableSelectionNodes(selectionRoot).map(selection => ({
                    ...selection,
                    modified: false,
                    type: SelectionType.Unsupervised,
                    rootName: selectionRoot.name,
                }))
            })
            .flat()

        const displaySelections: SelectionLayerInfo[] = [...currentSelections, ...savedSelections]

        const displayLayers = convertSelectionLayerInfoToSelectionLayers(
            displaySelections,
            lastResult,
            store
        )
        lastResult = displayLayers
        return displayLayers
    })
}

const convertSelectionLayerInfoToSelectionLayers = (
    displaySelections: SelectionLayerInfo[],
    lastResult: SelectionLayer[],
    store: Store
): SelectionLayer[] => {
    return displaySelections.map(selection => {
        const existingLayer = lastResult.find(
            (l: SelectionLayer) => l.name === selection.name && l.groupName === selection.rootName
        )
        if (existingLayer) {
            existingLayer.updateFromStore(selection)
            return existingLayer
        } else {
            return new SelectionLayer(selection, store)
        }
    })
}

export const selectFilterPoints = createSelector(
    selectSelectionState,
    (state: SelectionState): FilterPoints => {
        // Keys are `${segSource}:${segmentId}` so multi-map plots gate every map correctly.
        if (state.type === SelectionType.Free) {
            return { pointsThisLevel: null, pointsLowerLevels: null }
        } else if (state.type === SelectionType.Hierarchical) {
            if (state.hierarchicalSelection.selectedHierarchyName == undefined) {
                return {
                    pointsThisLevel: null,
                    pointsLowerLevels: getAllChildrenPointKeysInRootOrNode(
                        state.hierarchicalSelection
                    ),
                }
            }
            const selection = findSelectionNodeInRoot(
                state.hierarchicalSelection,
                state.hierarchicalSelection.selectedHierarchyName
            )
            return {
                pointsThisLevel: selection ? getPointKeysForSelection(selection) : null,
                pointsLowerLevels: selection
                    ? getAllChildrenPointKeysInRootOrNode(selection)
                    : null,
            }
        } else {
            if (state.unsupervisedSelection.selectedUnsupervisedName == undefined) {
                return {
                    pointsThisLevel: null,
                    pointsLowerLevels: getAllChildrenPointKeysInRootOrNode(
                        state.unsupervisedSelection
                    ),
                }
            }
            const selection = findSelectionNodeInRoot(
                state.unsupervisedSelection,
                state.unsupervisedSelection.selectedUnsupervisedName
            )
            return {
                pointsThisLevel: selection ? getPointKeysForSelection(selection) : null,
                pointsLowerLevels: selection
                    ? getAllChildrenPointKeysInRootOrNode(selection)
                    : null,
            }
        }
    }
)

export const selectDisplayedSelectionsOnScatterPlot = createSelector(
    selectSelectionState,
    (state: SelectionState): SelectionBase[] => {
        return state.displayedOnScatterPlot
    }
)

export interface FilterPoints {
    /** Selectable points at the current level as `${segSource}:${segmentId}` keys. */
    pointsThisLevel: Set<string> | null
    /** Points already sent to descendants (same key format). */
    pointsLowerLevels: Set<string> | null
}
